import { useState } from 'react';
import clsx from 'clsx';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Hourglass,
  CornerDownRight,
  Shield,
} from 'lucide-react';
import type { RuleVerdict, RuleState } from '@/lib/checkpoint/types';
import { HitDetail } from './HitDetail';

type RuleSeverity = 'pass' | 'mid' | 'fail';

/**
 * 状态视觉：
 *   - 注意 pending 的中文标签需要按 severity 动态决定（"等待中" vs "守护中"），
 *     所以这里只放图标 / 颜色，label 在 stateLabel() 中按规则上下文计算。
 */
const STATE_VIS: Record<RuleState, { icon: JSX.Element; color: string }> = {
  pending: { icon: <Hourglass className="h-3 w-3" />, color: 'text-ink-muted' },
  pass: { icon: <CheckCircle2 className="h-3 w-3" />, color: 'text-emerald-400' },
  mid: { icon: <AlertTriangle className="h-3 w-3" />, color: 'text-amber-400' },
  fail: { icon: <XCircle className="h-3 w-3" />, color: 'text-rose-400' },
  timeout: { icon: <Clock className="h-3 w-3" />, color: 'text-rose-400' },
};

/**
 * 把 (state, severity) 映射成中文标签。
 *
 * 关键点：当 state=pending 时，规则到底是"等"还是"守"取决于 severity：
 *   - severity=pass 表示规则期望命中（如 "Displayed"）→ pending = "等待中"
 *   - severity=fail/mid 表示规则期望不命中（如 "ANR"）→ pending = "守护中"
 *
 * 命中后的状态名沿用 通过 / 警告 / 失败 / 超时，保持和 SuiteCard 整体状态一致。
 */
function stateLabel(state: RuleState, severity: RuleSeverity): string {
  switch (state) {
    case 'pending':
      return severity === 'pass' ? '等待中' : '守护中';
    case 'pass':
      return '通过';
    case 'mid':
      return '警告';
    case 'fail':
      return '失败';
    case 'timeout':
      return '超时';
  }
}

/** severity 在 UI 上是"规则意图"标签 —— 用中文短词避免和 state 文案撞车（pass/mid/fail）。 */
const SEVERITY_META: Record<RuleSeverity, { label: string; cls: string; tip: string }> = {
  pass: {
    label: '期望',
    cls: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    tip: '期望命中：日志里出现这条匹配 = 通过',
  },
  mid: {
    label: '警示',
    cls: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
    tip: '警示规则：命中 = 警告',
  },
  fail: {
    label: '禁忌',
    cls: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
    tip: '禁忌命中：日志里出现 = 失败',
  },
};

interface Props {
  verdicts: RuleVerdict[];
  onJump?: (lineId: number) => void;
}

export function VerdictList({ verdicts, onJump }: Props) {
  return (
    <ul className="space-y-1">
      {verdicts.map((v) => (
        <VerdictRow key={v.ruleId} verdict={v} onJump={onJump} />
      ))}
    </ul>
  );
}

function VerdictRow({
  verdict,
  onJump,
}: {
  verdict: RuleVerdict;
  onJump?: (lineId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const vis = STATE_VIS[verdict.state];
  const sevMeta = SEVERITY_META[verdict.severity];
  const labelText = stateLabel(verdict.state, verdict.severity);
  const hasHit = !!verdict.lastHit && verdict.hitCount > 0;
  const lastLineId = verdict.lastHit?.lineId ?? -1;
  const canJump = hasHit && lastLineId >= 0 && !!onJump;
  return (
    <li className="rounded-md border border-line bg-bg-card">
      <div className="flex items-stretch text-[11px]">
        <button
          type="button"
          className="flex flex-1 items-center gap-1.5 px-2 py-1 text-left hover:bg-bg-soft"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <span
            className={clsx('inline-flex items-center gap-1 font-mono', vis.color)}
            title={`当前状态：${labelText}`}
          >
            {vis.icon}
            {labelText}
          </span>
          <span
            className={clsx('inline-flex items-center gap-0.5 rounded px-1 py-[1px] text-[10px]', sevMeta.cls)}
            title={sevMeta.tip}
          >
            <Shield className="h-2.5 w-2.5" />
            {sevMeta.label}
          </span>
          <span className="flex-1 truncate font-medium text-ink">{verdict.ruleName}</span>
          {verdict.hitCount > 0 && (
            <span className="font-mono text-[10px] text-ink-muted">×{verdict.hitCount}</span>
          )}
          {verdict.sequenceComplete === false && verdict.sequenceStepIndex !== undefined && (
            <span className="font-mono text-[10px] text-ink-muted">
              step {verdict.sequenceStepIndex}
            </span>
          )}
        </button>
        {canJump && (
          <button
            type="button"
            className="border-l border-line px-1.5 text-accent hover:bg-bg-soft"
            title="跳到日志中命中此规则的那一行"
            onClick={() => onJump?.(lastLineId)}
          >
            <CornerDownRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {open && (
        <div className="border-t border-line px-2 py-1.5 space-y-1">
          {hasHit ? (
            <HitDetail
              hit={verdict.lastHit!}
              onJump={canJump ? () => onJump?.(lastLineId) : undefined}
            />
          ) : (
            <p className="text-[11px] text-ink-muted">
              {labelText} —— {sevMeta.tip}
            </p>
          )}
        </div>
      )}
    </li>
  );
}
